import { matchPath, useLocation } from "react-router-dom";
import { IBreadcrumb } from "Interfaces";
import { useMemo } from "react";
import { useClientSelector } from "./useClientSelector";
import { useProjectSelector } from "./useProjectSelector";
import HomeIcon from "@mui/icons-material/Home";
import { useLocationSelector } from "./useLoationSelector";
import { useAudienceSelector } from "./useAudienceSelector";
import { useChannelSelector } from "Hooks/useChannelSelector";
import { useReportSelector } from "Hooks/useSelectedReport";
import { useChartSelector } from "Hooks/useSelectedChart";
import useProtectedRoutes from "./useProtectedRoutes";

const home: IBreadcrumb = { link: "/", text: "home", icon: HomeIcon };
const createBreadCrumb = (
  prev: string,
  pathName: string,
  clientId?: number,
  projectId?: number,
  protectedRoutes?: ReturnType<typeof useProtectedRoutes>["routes"]
) => {
  const arr = pathName.split("/");
  let crumbs: IBreadcrumb[] = [];
  const getLink = (index: number) => {
    const newarr = arr.slice(0, index + 1);
    return prev + newarr.join("/");
  };

  if (arr.length === 2 && arr[0] === "" && arr[1] === "") {
    return [];
  }
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];

    if (item !== "") {
      const link = getLink(i);
      const crumb: IBreadcrumb = { link, text: item, clientId, projectId };

      // Find matching route and add its breadcrumbComponent if it exists
      const matchingRoute = protectedRoutes?.find(
        route => route.path && matchPath(link, { path: route.path as string, exact: true })
      );
      if (matchingRoute?.breadcrumbComponent) {
        crumb.component = matchingRoute.breadcrumbComponent;
      }

      crumbs.push(crumb);
    }
  }
  return crumbs;
};

export const useBreadcrumb = () => {
  const location = useLocation();
  const { routes: protectedRoutes } = useProtectedRoutes();
  const client = useClientSelector();
  const project = useProjectSelector();
  const selectedLocation = useLocationSelector();
  const selectedAudience = useAudienceSelector();
  const selectedChanel = useChannelSelector();
  const selectedReport = useReportSelector();
  const selectedChart = useChartSelector();

  const isProjectPath = matchPath<{ clientId: string; projectId: string }>(
    location.pathname,
    {
      path: "/clients/:clientId/projects/:projectId",
      exact: false,
      strict: false,
    }
  );
  const isAdminPath = location.pathname.startsWith("/admin");
  return useMemo((): IBreadcrumb[] => {
    let crumb: IBreadcrumb[];
    if (isProjectPath && client && project) {
      // Home is the project root; no separate client or project crumbs
      crumb = [
        {
          ...home,
          link: `/clients/${client.id}/projects/${project.id}`,
          clientId: client.id,
          projectId: project.id,
        },
      ];
      const splits = location.pathname.split(isProjectPath.url);

      const parts = splits[1].split("/");
      const extras = createBreadCrumb(
        isProjectPath.url,
        parts
          .map((p, i) => {
            if (parts[i - 1] === "chart-library" && selectedChart)
              return selectedChart.name || "unknown";
            if (parts[i - 1] === "reports" && selectedReport)
              return selectedReport.name || "unknown";
            if (parts[i - 1] === "channels" && selectedChanel)
              return selectedChanel.name || "unknown";
            if (parts[i - 1] === "audiences" && selectedAudience)
              return selectedAudience.name || "unknown";
            if (parts[i - 1] === "locations" && selectedLocation)
              return selectedLocation.name || "unknown";
            return p;
          })
          .join("/"),
        client?.id,
        project?.id,
        protectedRoutes
      );
      crumb = [...crumb, ...extras];
    } else if (isAdminPath) {
      // Handle admin routes
      crumb = [home];
      const parts = location.pathname.split("/").filter(p => p !== "");
      const extras = createBreadCrumb(
        "",
        parts.join("/"),
        undefined,
        undefined,
        protectedRoutes
      );
      crumb = [...crumb, ...extras];
    } else {
      crumb = [home];
    }

    // Check if the current full path matches any protected route and add its breadcrumbComponent
    const currentRoute = protectedRoutes?.find(
      route => route.path && matchPath(location.pathname, { path: route.path as string, exact: true })
    );
    if (currentRoute?.breadcrumbComponent && crumb.length > 0) {
      crumb[crumb.length - 1].component = currentRoute.breadcrumbComponent;
    }

    return crumb;
  }, [
    isProjectPath,
    isAdminPath,
    client,
    project,
    location,
    selectedLocation,
    selectedAudience,
    selectedChanel,
    selectedReport,
    selectedChart,
    protectedRoutes,
  ]);
};
